import { useState, useEffect } from 'react';

/**
 * Subscribe to a CSS media query and return whether it currently matches.
 *
 * Re-renders the consuming component whenever the match state changes (e.g.
 * the browser window is resized across the query boundary or the device is
 * rotated). SSR/test safe: returns `false` when `window.matchMedia` is
 * unavailable.
 *
 * @param query a CSS media query string, e.g. `(max-width: 767px)`
 */
export default function useMediaQuery(query: string): boolean {
  const getMatches = (q: string): boolean => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false;
    }
    return window.matchMedia(q).matches;
  };

  const [matches, setMatches] = useState<boolean>(() => getMatches(query));

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }

    const mediaQueryList = window.matchMedia(query);
    const handleChange = (event: MediaQueryListEvent) => setMatches(event.matches);

    // Sync in case the value changed between the initial render and this effect.
    setMatches(mediaQueryList.matches);

    // `addEventListener` is the modern API; fall back to the deprecated
    // `addListener` for older Safari versions.
    if (typeof mediaQueryList.addEventListener === 'function') {
      mediaQueryList.addEventListener('change', handleChange);
      return () => mediaQueryList.removeEventListener('change', handleChange);
    }

    mediaQueryList.addListener(handleChange);
    return () => mediaQueryList.removeListener(handleChange);
  }, [query]);

  return matches;
}
